import { brand } from '../../config/brand';

/**
 * The logo, drawn in the document rather than loaded as an image.
 *
 * ## Why it is inline
 *
 * `/brand/logo.svg` bakes its colours in, and an SVG loaded through `<img>` is
 * an isolated document - our custom properties do not reach it. That was
 * invisible while there was one theme and fatal the moment there were two: the
 * stem is `#EDEDF2`, chosen to be near-white on near-black, and on a light
 * page it disappeared entirely. The suffix is Signal, which is a token for the
 * same reason.
 *
 * Inline, every fill is a token, so the mark follows the theme like everything
 * else. The file stays where it is - it is still the right thing to hand to an
 * email client, an `og:image`, or anyone asking for the logo.
 *
 * ## The bars are the spectrum
 *
 * Same five colours as the loading screens, and they come from the same tokens,
 * so a theme that darkens them darkens all three surfaces together instead of
 * leaving the logo bright against a lightened one.
 */
/**
 * The mark, split where it changes colour.
 *
 * The suffix carries the accent and the stem does not, so the component needs
 * the two halves rather than the string. Splitting at the first dot keeps the
 * literal out of here - the file that is not allowed to know the name should
 * not know how it is spelled either - and a mark with no dot in it simply
 * renders as one piece.
 */
function halves(mark: string): [string, string] {
  const at = mark.indexOf('.');

  return at === -1 ? [mark, ''] : [mark.slice(0, at), mark.slice(at)];
}

export function Wordmark({
  className = '',
  markOnly = false,
}: {
  className?: string;
  /**
   * Just the bars, no lettering.
   *
   * For a bar narrow enough that the name and the controls cannot both fit -
   * a phone header, where the full lockup is 125px of a 390px screen and the
   * things somebody came to press are on the other side of it. The mark alone
   * still identifies the place; a name that pushes the primary action off the
   * edge does not.
   */
  markOnly?: boolean;
}) {
  const [stem, suffix] = halves(brand.wordmark);

  return (
    <svg
      viewBox={markOnly ? '0 0 96 120' : '0 0 470 120'}
      className={className}
      role="img"
      aria-label={brand.name}
      data-wordmark
    >
      <g transform="translate(0,12)">
        <rect width="96" height="96" rx="26" fill="var(--color-slate-2)" />
        <rect x="20" y="20" width="5.6" height="56" rx="2.8" fill="var(--color-signal)" />
        <rect x="32.6" y="31.2" width="5.6" height="33.6" rx="2.8" fill="var(--color-violet)" />
        <rect x="45.2" y="39.6" width="5.6" height="16.8" rx="2.8" fill="var(--color-hot)" />
        <rect x="57.8" y="31.2" width="5.6" height="33.6" rx="2.8" fill="var(--color-teal)" />
        <rect x="70.4" y="20" width="5.6" height="56" rx="2.8" fill="var(--color-fresh)" />
      </g>
      {!markOnly && (
      <text
        x="126"
        y="60"
        dominantBaseline="central"
        /* The stack the asset carries, kept identical so inlining did not
           quietly restyle the one piece of type nobody would think to check. */
        fontFamily="Space Grotesk, Helvetica, Arial, sans-serif"
        fontWeight="700"
        fontSize="58"
        letterSpacing="-0.6"
      >
        <tspan fill="var(--color-text)">{stem}</tspan>
        {suffix !== '' && <tspan fill="var(--color-signal)">{suffix}</tspan>}
      </text>
      )}
    </svg>
  );
}
